import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { Link, useNavigate } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { Bell, ChevronRight, Download, LogOut, Monitor, Moon, Pencil, Plus, Share, SquarePlus, Sun } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { renderSVG } from "uqr";
import { PageHeader } from "@/components/AppShell";
import { Sheet } from "@/components/Sheet";
import { SwitchToKidSheet } from "@/components/SwitchUser";
import { Badge, Button, Card, Field, Input, ListLink, SectionTitle, Segmented, Select, TextField, Toggle, cx } from "@/components/ui";
import { Avatar, IconPicker } from "@/components/bits";
import { useToast } from "@/components/Toast";
import { api, type ApiError } from "@/lib/api";
import { useAction, useNotifications } from "@/lib/queries";
import { useLogout, useSession } from "@/lib/session";
import { disablePush, enablePush, pushEnabled, pushSupported, useInstall } from "@/lib/pwa";
import { isNative } from "@/lib/native";
import { CURRENCY_PRESETS, PARENT_AVATARS } from "@shared/templates";
import { isValidTimezone } from "@shared/time";
import type { BillingSummaryDTO, FamilyDTO, KidDeviceDTO, MeDTO } from "@shared/types";

// ---------- local helpers ----------

function deviceTimezone(): string | null {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
}

function timezoneList(): string[] {
  try {
    const intl = Intl as typeof Intl & { supportedValuesOf?: (key: string) => string[] };
    return typeof intl.supportedValuesOf === "function" ? intl.supportedValuesOf("timeZone") : [];
  } catch {
    return [];
  }
}

type Theme = "system" | "light" | "dark";
const THEME_KEY = "mr.theme";

function readTheme(): Theme {
  try {
    const v = localStorage.getItem(THEME_KEY);
    return v === "light" || v === "dark" ? v : "system";
  } catch {
    return "system";
  }
}

function applyTheme(theme: Theme) {
  const root = document.documentElement;
  if (theme === "system") delete root.dataset.theme;
  else root.dataset.theme = theme;
  try {
    if (theme === "system") localStorage.removeItem(THEME_KEY);
    else localStorage.setItem(THEME_KEY, theme);
  } catch {
    /* storage unavailable — the choice still applies for this visit */
  }
}

// ---------- page ----------

function planSubtitle(b: BillingSummaryDTO): string {
  if (b.exempt) return "Complimentary access";
  if (b.status === "trialing" && b.trialEndsAt) {
    const days = Math.max(0, Math.ceil((b.trialEndsAt - Date.now()) / 86_400_000));
    return `Free trial · ${days} ${days === 1 ? "day" : "days"} left`;
  }
  if (b.status === "past_due") return "Payment needs attention";
  if (b.cancelAtPeriodEnd) return "Cancelled · ends soon";
  return b.plan === "annual" ? "Annual plan" : "Quarterly plan";
}

export function More() {
  const session = useSession();
  const navigate = useNavigate();
  const logout = useLogout();
  const { data: notes } = useNotifications();
  const [signingOut, setSigningOut] = useState(false);
  const [switching, setSwitching] = useState(false);

  async function signOut() {
    setSigningOut(true);
    await logout();
    navigate("/", { replace: true });
  }

  const kidCount = session.kids.length;
  const parentCount = session.parents.length;

  return (
    <div>
      <PageHeader title="Settings" bell="/parent/notifications" />

      <ProfileCard />

      <Card className="mt-4 p-1.5">
        {kidCount > 0 && (
          <button
            type="button"
            onClick={() => setSwitching(true)}
            className="flex w-full items-center gap-3 rounded-2xl px-3 py-3 text-left transition hover:bg-sunken"
          >
            <span className="flex size-10 shrink-0 items-center justify-center rounded-2xl bg-sunken text-xl">🔁</span>
            <span className="min-w-0 flex-1">
              <span className="block font-bold">Switch to a kid</span>
              <span className="block truncate text-sm text-muted">Hand this device to a kid; come back with your passkey or password</span>
            </span>
            <ChevronRight className="size-5 text-faint" />
          </button>
        )}
        <ListLink
          to="/parent/family"
          icon="👨‍👩‍👧"
          title="Family & kids"
          subtitle={`${kidCount} ${kidCount === 1 ? "kid" : "kids"} · ${parentCount} ${parentCount === 1 ? "parent" : "parents"}`}
        />
        {session.me.billing.enabled && (
          <ListLink to="/parent/billing" icon="💳" title="Plan & billing" subtitle={planSubtitle(session.me.billing)} />
        )}
        <ListLink
          to="/parent/privacy"
          icon="🔒"
          title="Privacy & security"
          subtitle={session.me.privacy.consent !== "granted" ? "Consent needed for kids to sign in" : session.me.privacy.mfaEnabled ? "Two-step verification on" : "Turn on two-step verification"}
          trailing={session.me.privacy.consent !== "granted" ? <Badge tone="danger">Action needed</Badge> : undefined}
        />
        <ListLink to="/parent/integrations" icon="✨" title="AI assistant & integrations" subtitle="Optional: set up by chat · automatic time enforcement" />
        <ListLink
          to="/parent/notifications"
          icon={<Bell className="size-5" />}
          title="Notifications"
          trailing={notes?.unread ? <Badge tone="danger">{notes.unread} new</Badge> : undefined}
        />
      </Card>

      <SwitchToKidSheet open={switching} onClose={() => setSwitching(false)} />

      <SectionTitle>Family settings</SectionTitle>
      <FamilySettingsCard family={session.family} />

      <SectionTitle>Kid devices</SectionTitle>
      <KidDevicesCard />

      <SectionTitle>Install the app</SectionTitle>
      <InstallCard />

      <SectionTitle>Notifications</SectionTitle>
      <PushCard />

      <SectionTitle>Appearance</SectionTitle>
      <ThemeCard />

      <SectionTitle>About</SectionTitle>
      <AboutCard />

      <SectionTitle>Your data</SectionTitle>
      <DataCard />

      <Button variant="secondary" size="lg" block className="mt-8" onClick={signOut} loading={signingOut}>
        <LogOut className="size-5 text-danger" /> Sign out
      </Button>
    </div>
  );
}

// ---------- profile ----------

interface ProfileDraft {
  name: string;
  avatar: string;
  currentPassword: string;
  newPassword: string;
}

function ProfileCard() {
  const { user } = useSession();
  const qc = useQueryClient();
  const [draft, setDraft] = useState<ProfileDraft | null>(null);
  // Stable close handler: the Sheet re-runs its open effect (and refocuses) when onClose changes.
  const close = useCallback(() => setDraft(null), []);

  const save = useAction(
    (d: ProfileDraft) => {
      const body: Record<string, string> = {};
      if (d.name.trim() !== user.name) body.name = d.name.trim();
      if (d.avatar !== user.avatar) body.avatar = d.avatar;
      if (d.newPassword) {
        body.currentPassword = d.currentPassword;
        body.newPassword = d.newPassword;
      }
      return api.patch<MeDTO>("/family/me", body);
    },
    {
      success: "Profile saved",
      onSuccess: (me) => {
        qc.setQueryData(["me"], me);
        setDraft(null);
      },
    },
  );

  const passwordError = draft?.newPassword && draft.newPassword.length < 8 ? "At least 8 characters" : null;
  const needsCurrent = !!draft?.newPassword && !draft.currentPassword;
  const dirty =
    !!draft && (draft.name.trim() !== user.name || draft.avatar !== user.avatar || !!draft.newPassword);
  const valid = !!draft?.name.trim() && !passwordError && !needsCurrent;

  return (
    <>
      <Card className="mt-2 flex items-center gap-4">
        <Avatar member={user} size="lg" />
        <div className="min-w-0 flex-1">
          <div className="truncate text-lg font-extrabold">{user.name}</div>
          {user.email && <div className="truncate text-sm text-muted">{user.email}</div>}
        </div>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => setDraft({ name: user.name, avatar: user.avatar, currentPassword: "", newPassword: "" })}
        >
          <Pencil className="size-4" /> Edit
        </Button>
      </Card>

      <Sheet
        open={!!draft}
        onClose={close}
        title="Your profile"
        footer={
          <Button block onClick={() => draft && save.mutate(draft)} loading={save.isPending} disabled={!dirty || !valid}>
            Save
          </Button>
        }
      >
        {draft && (
          <div className="space-y-5">
            <div className="flex justify-center pt-1">
              <Avatar member={{ ...user, avatar: draft.avatar, name: draft.name || user.name }} size="xl" />
            </div>
            <TextField
              label="Your name"
              value={draft.name}
              maxLength={40}
              autoComplete="name"
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            />
            <Field label="Avatar">
              <IconPicker value={draft.avatar} choices={PARENT_AVATARS} onChange={(avatar) => setDraft({ ...draft, avatar })} />
            </Field>
            <div className="space-y-3 rounded-3xl bg-sunken/60 p-4 ring-1 ring-line/60">
              <div>
                <div className="font-extrabold">Change password</div>
                <p className="text-sm text-muted">Leave blank to keep your current one. Other devices will be signed out.</p>
              </div>
              <TextField
                label="Current password"
                type="password"
                autoComplete="current-password"
                value={draft.currentPassword}
                onChange={(e) => setDraft({ ...draft, currentPassword: e.target.value })}
                error={needsCurrent ? "Enter your current password to change it" : null}
              />
              <TextField
                label="New password"
                type="password"
                autoComplete="new-password"
                value={draft.newPassword}
                onChange={(e) => setDraft({ ...draft, newPassword: e.target.value })}
                error={passwordError}
                hint="At least 8 characters."
              />
            </div>
          </div>
        )}
      </Sheet>
    </>
  );
}

// ---------- family settings ----------

type FamilyFields = Pick<FamilyDTO, "name" | "currencyName" | "currencyIcon" | "timezone" | "weekStartsOn" | "responsibilityMode">;

function pickFamily(f: FamilyDTO): FamilyFields {
  return {
    name: f.name,
    currencyName: f.currencyName,
    currencyIcon: f.currencyIcon,
    timezone: f.timezone,
    weekStartsOn: f.weekStartsOn,
    responsibilityMode: f.responsibilityMode,
  };
}

function FamilySettingsCard({ family }: { family: FamilyDTO }) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState<FamilyFields>(() => pickFamily(family));
  const saved = pickFamily(family);
  const allZones = useMemo(timezoneList, []);
  const device = useMemo(deviceTimezone, []);
  // Make sure the saved, selected and device zones are always choosable (e.g. "UTC" isn't in every browser's list).
  const zones = allZones.length
    ? [...new Set([draft.timezone, saved.timezone, device].filter((z): z is string => !!z && !allZones.includes(z))), ...allZones]
    : allZones;

  const changes = (Object.keys(draft) as (keyof FamilyFields)[]).reduce<Partial<FamilyFields>>((acc, key) => {
    const value = typeof draft[key] === "string" ? (draft[key] as string).trim() : draft[key];
    if (value !== saved[key]) (acc as Record<string, unknown>)[key] = value;
    return acc;
  }, {});
  const dirty = Object.keys(changes).length > 0;
  const tzValid = isValidTimezone(draft.timezone.trim());
  const valid = !!draft.name.trim() && !!draft.currencyName.trim() && !!draft.currencyIcon.trim() && tzValid;

  const save = useAction((body: Partial<FamilyFields>) => api.patch<MeDTO>("/family", body), {
    success: "Family settings saved",
    onSuccess: (me) => {
      qc.setQueryData(["me"], me);
      setDraft(pickFamily(me.family));
    },
  });

  const set = <K extends keyof FamilyFields>(key: K, value: FamilyFields[K]) => setDraft((d) => ({ ...d, [key]: value }));

  return (
    <Card className="space-y-5">
      <TextField label="Family name" value={draft.name} maxLength={60} onChange={(e) => set("name", e.target.value)} />

      <Field label="Currency" hint="What kids earn for jobs. Pick one or make up your own.">
        <div className="flex flex-wrap gap-2 pb-1">
          {CURRENCY_PRESETS.map((p) => {
            const active = draft.currencyIcon === p.icon && draft.currencyName === p.name;
            return (
              <button
                key={p.name}
                type="button"
                aria-pressed={active}
                onClick={() => setDraft((d) => ({ ...d, currencyIcon: p.icon, currencyName: p.name }))}
                className={cx(
                  "inline-flex items-center gap-1.5 rounded-full px-3 py-2 text-sm font-bold transition",
                  active ? "bg-brand text-on-brand" : "bg-sunken text-ink ring-1 ring-line hover:ring-brand",
                )}
              >
                <span aria-hidden>{p.icon}</span> {p.name}
              </button>
            );
          })}
        </div>
        <div className="flex gap-2">
          <Input
            aria-label="Currency icon"
            className="!w-20 shrink-0 text-center text-xl"
            value={draft.currencyIcon}
            maxLength={16}
            onChange={(e) => set("currencyIcon", e.target.value)}
          />
          <Input
            aria-label="Currency name"
            className="min-w-0 flex-1"
            placeholder="Coins"
            value={draft.currencyName}
            maxLength={20}
            onChange={(e) => set("currencyName", e.target.value)}
          />
        </div>
      </Field>

      <Field
        label="Timezone"
        hint="Daily responsibilities and caps reset at midnight here."
        error={!tzValid && draft.timezone.trim() ? "That timezone isn't recognized" : null}
      >
        {zones.length ? (
          <Select aria-label="Timezone" value={draft.timezone} onChange={(e) => set("timezone", e.target.value)}>
            {zones.map((z) => (
              <option key={z} value={z}>
                {z.replace(/_/g, " ")}
              </option>
            ))}
          </Select>
        ) : (
          <Input
            aria-label="Timezone"
            placeholder="America/New_York"
            value={draft.timezone}
            onChange={(e) => set("timezone", e.target.value)}
          />
        )}
        {device && device !== draft.timezone && (
          <button type="button" className="text-sm font-bold text-brand" onClick={() => set("timezone", device)}>
            Use this device's timezone ({device.replace(/_/g, " ")})
          </button>
        )}
      </Field>

      <Field label="Week starts on" hint="When weekly responsibilities, jobs and caps reset.">
        <Segmented
          value={String(draft.weekStartsOn)}
          onChange={(v) => set("weekStartsOn", Number(v))}
          options={[
            { value: "0", label: "Sunday" },
            { value: "1", label: "Monday" },
          ]}
        />
      </Field>

      <Field label="Responsibility check-offs">
        <Segmented
          value={draft.responsibilityMode}
          onChange={(v) => set("responsibilityMode", v)}
          options={[
            { value: "trust", label: "🤝 Trust" },
            { value: "verify", label: "🔍 Verify" },
          ]}
        />
        <p className="text-sm text-muted">
          {draft.responsibilityMode === "trust"
            ? "Kids check off their own responsibilities and jobs unlock right away."
            : "A parent confirms each check-off before jobs unlock. Great while habits are forming."}
        </p>
      </Field>

      <div className="flex gap-2">
        {dirty && (
          <Button variant="ghost" onClick={() => setDraft(pickFamily(family))}>
            Undo
          </Button>
        )}
        <Button block onClick={() => save.mutate(changes)} loading={save.isPending} disabled={!dirty || !valid}>
          Save settings
        </Button>
      </div>
    </Card>
  );
}

// ---------- kid devices ----------

function KidDevicesCard() {
  const qc = useQueryClient();
  const devices = useQuery<{ devices: KidDeviceDTO[] }, ApiError>({ queryKey: ["kid-devices"], queryFn: () => api.get("/family/devices") });
  const [pairing, setPairing] = useState<{ code: string; expiresAt: number; url: string } | null>(null);
  const close = useCallback(() => setPairing(null), []);
  const create = useAction(() => api.post<{ code: string; expiresAt: number; url: string }>("/family/devices/pair"), {
    onSuccess: (p) => setPairing(p),
  });
  const unpair = useAction((id: string) => api.del(`/family/devices/${id}`), {
    success: "Device unpaired",
    onSuccess: () => qc.invalidateQueries({ queryKey: ["kid-devices"] }),
  });
  const qr = useMemo(() => (pairing ? renderSVG(pairing.url, { border: 1 }) : ""), [pairing]);
  const list = devices.data?.devices ?? [];

  return (
    <Card className="space-y-3">
      <p className="text-sm text-muted">
        Kids sign in with their PIN only on devices you pair. Pair each phone, tablet or computer your kids use, and unpair any device that's lost or
        given away.
      </p>
      {list.length > 0 && (
        <ul className="divide-y divide-line/70">
          {list.map((d) => (
            <li key={d.id} className="flex items-center gap-3 py-2">
              <span className="text-xl" aria-hidden>
                📱
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate font-bold">{d.label}</p>
                <p className="text-xs text-muted">
                  Paired {new Date(d.createdAt).toLocaleDateString()}
                  {d.pairedBy ? ` by ${d.pairedBy}` : ""}
                </p>
              </div>
              <Button
                size="sm"
                variant="ghost"
                className="text-danger"
                onClick={() => window.confirm(`Unpair ${d.label}? Kids signed in on it will be signed out.`) && unpair.mutate(d.id)}
              >
                Unpair
              </Button>
            </li>
          ))}
        </ul>
      )}
      <Button onClick={() => create.mutate()} loading={create.isPending}>
        <Plus className="size-4" /> Pair a device
      </Button>
      <Sheet open={!!pairing} onClose={close} title="Pair a kid's device">
        {pairing && (
          <div className="space-y-4 text-center">
            <p className="text-sm text-muted">
              On your kid's device, open Meaningful Rewards → <b className="text-ink">I'm a kid</b> and enter this code, or scan the QR code with its
              camera.
            </p>
            <p className="select-all font-mono text-4xl font-black tracking-[0.15em] text-brand-strong">{pairing.code}</p>
            <div className="mx-auto w-44 rounded-2xl bg-white p-2" dangerouslySetInnerHTML={{ __html: qr }} />
            <p className="text-xs text-muted">Works once, for the next 15 minutes.</p>
          </div>
        )}
      </Sheet>
    </Card>
  );
}

// ---------- install ----------

function InstallCard() {
  const install = useInstall();
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  if (install.installed) {
    return (
      <Card className="flex items-center gap-3">
        <span className="flex size-11 shrink-0 items-center justify-center rounded-2xl bg-brand-soft text-2xl">📱</span>
        <div>
          <div className="font-extrabold">You're using the app</div>
          <p className="text-sm text-muted">Meaningful Rewards is installed on this device.</p>
        </div>
      </Card>
    );
  }

  if (install.canPrompt) {
    return (
      <Card className="flex flex-wrap items-center gap-3">
        <span className="flex size-11 shrink-0 items-center justify-center rounded-2xl bg-brand-soft text-2xl">📲</span>
        <div className="min-w-0 flex-1">
          <div className="font-extrabold">Add it to your home screen</div>
          <p className="text-sm text-muted">Opens full-screen, works offline and can send notifications.</p>
        </div>
        <Button
          loading={busy}
          onClick={async () => {
            setBusy(true);
            try {
              if (await install.prompt()) toast.show("Installed — find it on your home screen", "success");
            } finally {
              setBusy(false);
            }
          }}
        >
          <Download className="size-4" /> Install
        </Button>
      </Card>
    );
  }

  if (install.ios) {
    return (
      <Card>
        <div className="font-extrabold">Add it to your Home Screen</div>
        <ol className="mt-3 space-y-2.5 text-sm">
          <Step n={1}>
            Open this page in <b>Safari</b> and tap <b>Share</b> <Share className="inline size-4 align-[-3px] text-brand" />
          </Step>
          <Step n={2}>
            Scroll down and tap <b>Add to Home Screen</b> <SquarePlus className="inline size-4 align-[-3px] text-brand" />
          </Step>
          <Step n={3}>
            Tap <b>Add</b> — then open Meaningful Rewards from your Home Screen.
          </Step>
        </ol>
      </Card>
    );
  }

  return (
    <Card>
      <div className="font-extrabold">Install from your browser</div>
      <p className="mt-1 text-sm text-muted">
        Open your browser's menu (<b className="text-ink">⋮</b> or <b className="text-ink">⋯</b>) and choose{" "}
        <b className="text-ink">Install app</b> or <b className="text-ink">Add to Home screen</b>. It opens full-screen and can send
        notifications.
      </p>
    </Card>
  );
}

function Step({ n, children }: { n: number; children: ReactNode }) {
  return (
    <li className="flex items-start gap-3">
      <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-brand-soft text-xs font-extrabold text-brand-strong">
        {n}
      </span>
      <span className="pt-0.5">{children}</span>
    </li>
  );
}

// ---------- push notifications ----------

function PushCard() {
  const { user } = useSession();
  const install = useInstall();
  const toast = useToast();
  const supported = pushSupported();
  const [enabled, setEnabled] = useState<boolean | null>(supported ? null : false);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  useEffect(() => {
    if (!supported) return;
    let cancelled = false;
    pushEnabled(user.id)
      .then((on) => !cancelled && setEnabled(on))
      .catch(() => !cancelled && setEnabled(false));
    if (!isNative() && typeof Notification !== "undefined" && Notification.permission === "denied") {
      setProblem("Notifications are blocked for this app. Allow them in your browser or phone settings, then try again.");
    }
    return () => {
      cancelled = true;
    };
  }, [supported, user.id]);

  async function toggle(on: boolean) {
    setBusy(true);
    setProblem(null);
    try {
      if (on) {
        const result = await enablePush(user.id);
        if (result === "enabled") {
          setEnabled(true);
          toast.show("Notifications are on for this device", "success");
        } else if (result === "denied") {
          setEnabled(false);
          setProblem("Notifications are blocked for this app. Allow them in your browser or phone settings, then try again.");
        } else {
          setEnabled(false);
          setProblem("Push notifications aren't set up on this server yet. You'll still see everything under the bell.");
        }
      } else {
        await disablePush();
        setEnabled(false);
        toast.show("Notifications are off for this device", "success");
      }
    } catch (err) {
      toast.show((err as Error).message || "Couldn't change notifications", "error");
    } finally {
      setBusy(false);
    }
  }

  const iosNeedsInstall = install.ios && !install.installed;

  return (
    <Card>
      {supported ? (
        <Toggle
          checked={!!enabled}
          disabled={busy || enabled === null}
          onChange={toggle}
          label="Push notifications on this device"
          description="Get a ping when kids finish jobs, ask for rewards or pitch ideas."
        />
      ) : (
        <div>
          <div className="font-bold">Push notifications</div>
          <p className="mt-0.5 text-sm text-muted">
            {iosNeedsInstall
              ? "On iPhone and iPad, add the app to your Home Screen first (iOS 16.4 or later), then turn notifications on here."
              : "This browser doesn't support push notifications. You'll still see everything under the bell."}
          </p>
        </div>
      )}
      {problem && <p className="mt-3 rounded-2xl bg-danger-soft px-3 py-2 text-sm font-semibold text-danger">{problem}</p>}
      {supported && iosNeedsInstall && (
        <p className="mt-3 text-xs text-muted">On iPhone and iPad, push only works once the app is on your Home Screen (iOS 16.4+).</p>
      )}
    </Card>
  );
}

// ---------- appearance ----------

function ThemeCard() {
  const [theme, setTheme] = useState<Theme>(readTheme);
  return (
    <Card>
      <Segmented
        value={theme}
        onChange={(t) => {
          setTheme(t);
          applyTheme(t);
        }}
        options={[
          {
            value: "system",
            label: (
              <span className="inline-flex items-center gap-1.5">
                <Monitor className="size-4" /> System
              </span>
            ),
          },
          {
            value: "light",
            label: (
              <span className="inline-flex items-center gap-1.5">
                <Sun className="size-4" /> Light
              </span>
            ),
          },
          {
            value: "dark",
            label: (
              <span className="inline-flex items-center gap-1.5">
                <Moon className="size-4" /> Dark
              </span>
            ),
          },
        ]}
      />
      <p className="mt-2 text-xs text-muted">Saved on this device.</p>
    </Card>
  );
}

// ---------- about ----------

function AboutCard() {
  return (
    <Card className="space-y-2 text-sm">
      <div className="flex items-center justify-between gap-3">
        <span className="font-bold">Advisor</span>
        <Badge tone="neutral">Built-in suggestions</Badge>
      </div>
      <p className="text-muted">
        🔒 There's no AI in Meaningful Rewards. Suggestions come from built-in rules on our servers, and nothing about your family is sent to an AI
        service. If you want help from an assistant, you can connect your own under AI assistant &amp; integrations.
      </p>
      <p>
        <Link to="/privacy" className="font-bold text-brand">
          Privacy Policy
        </Link>
        {" · "}
        <Link to="/terms" className="font-bold text-brand">
          Terms of Service
        </Link>
      </p>
    </Card>
  );
}

// ---------- data export / deletion ----------

export function DataCard() {
  const { family, me } = useSession();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState("");
  const [password, setPassword] = useState("");
  const remove = useAction(() => api.post("/family/delete", { confirm, password }), {
    success: "Your family account was deleted",
    onSuccess: () => {
      qc.setQueryData(["me"], null);
      qc.clear();
      navigate("/", { replace: true });
    },
  });
  return (
    <Card className="space-y-3">
      <p className="text-sm text-muted">Download everything stored for your family, or delete it all permanently.</p>
      <div className="flex flex-wrap gap-2">
        <a href="/api/family/export" download>
          <Button variant="secondary" size="sm">
            <Download className="size-4" /> Export family data
          </Button>
        </a>
        <Button variant="ghost" size="sm" className="text-danger" onClick={() => setOpen(true)}>
          Delete family…
        </Button>
      </div>
      <Sheet
        open={open}
        onClose={() => setOpen(false)}
        title="Delete your family?"
        footer={
          <Button block variant="danger" onClick={() => remove.mutate()} loading={remove.isPending} disabled={confirm.trim() !== family.name.trim() || !password}>
            Permanently delete
          </Button>
        }
      >
        <p className="text-sm text-muted">
          This removes every parent and kid account, all balances, history, goals and settings, and cancels your subscription. It can't be undone —
          export your data first if you want a copy.
        </p>
        {me.billing.source === "apple" && (
          <p className="mt-2 text-sm font-bold">Apple doesn't let us cancel App Store subscriptions. Cancel yours in your Apple account settings too, or Apple keeps charging you.</p>
        )}
        <div className="mt-4 space-y-4">
          <TextField label={`Type “${family.name}” to confirm`} value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="off" />
          <TextField label="Your password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} />
        </div>
      </Sheet>
    </Card>
  );
}
